import React, { useRef } from 'react';
import { Film, ImageIcon, X, Info, Loader2, Upload, Play } from 'lucide-react';
import { InfoTooltip } from './InfoTooltip';
import {
  REELS_MAX_DURATION_SECONDS,
  REELS_RECOMMENDED_ASPECT_RATIO,
  REELS_SUPPORTED_VIDEO_FORMATS,
  REELS_MAX_CAPTION_LENGTH,
} from './ReelsStoryConstants';

interface ReelsMediaSelectorProps {
  videoUrl: string | null;
  coverImageUrl: string | null;
  isUploading: boolean;
  onVideoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCoverImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVideoRemove: () => void;
  onCoverImageRemove: () => void;
  coverOnly?: boolean;
}

export const ReelsMediaSelector: React.FC<ReelsMediaSelectorProps> = ({
  videoUrl,
  coverImageUrl,
  isUploading,
  onVideoChange,
  onCoverImageChange,
  onVideoRemove,
  onCoverImageRemove,
  coverOnly = false,
}) => {
  const videoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-3">
      {/* Side-by-side layout: Video + Cover */}
      <div className="flex gap-4">
        {/* Video Section */}
        {!coverOnly && (
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Film className="w-3.5 h-3.5 text-purple-600" />
            <span>Wideo do Reel</span>
            <span className="text-red-500">*</span>
            <InfoTooltip
              text={`Upload video in ${REELS_SUPPORTED_VIDEO_FORMATS.join('/')} format. Max ${REELS_MAX_DURATION_SECONDS}s, aspect ratio ${REELS_RECOMMENDED_ASPECT_RATIO}`}
              position="bottom"
            />
          </label>

          {!videoUrl ? (
            <div
              onClick={() => !isUploading && videoInputRef.current?.click()}
              className={`
                relative aspect-[9/16] max-h-72 rounded-2xl overflow-hidden transition-all
                border-2 border-dashed cursor-pointer
                ${isUploading
                  ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                  : 'border-purple-300 bg-gradient-to-b from-purple-50/50 to-purple-100/30 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-100'
                }
              `}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
                {isUploading ? (
                  <>
                    <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
                    <span className="text-xs text-gray-500 font-medium">Przesyłanie...</span>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                      <Upload className="w-7 h-7 text-purple-500" />
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-medium text-purple-700 block">Dodaj wideo</span>
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        MP4/MOV · Max {REELS_MAX_DURATION_SECONDS}s
                      </span>
                    </div>
                  </>
                )}
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/*"
                className="hidden"
                onChange={onVideoChange}
                disabled={isUploading}
              />
            </div>
          ) : (
            <div className="relative aspect-[9/16] max-h-72 rounded-2xl overflow-hidden bg-black shadow-lg ring-2 ring-purple-200">
              <video
                src={videoUrl}
                controls
                className="w-full h-full object-contain"
              />
              <button
                type="button"
                onClick={onVideoRemove}
                disabled={isUploading}
                className="absolute top-2 right-2 bg-red-500/90 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg transition-all hover:scale-110"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              {/* Video badge */}
              <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                <Play className="w-2.5 h-2.5 fill-current" />
                Reel
              </div>
            </div>
          )}
        </div>
        )}

        {/* Cover Image Section */}
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5 text-purple-600" />
            <span>Okładka</span>
            <span className="text-[10px] text-gray-400 font-normal normal-case">(opcjonalnie)</span>
            <InfoTooltip
              text="Cover image displayed before the reel plays. Recommended 1080x1920px."
              position="bottom"
            />
          </label>

          {!coverImageUrl ? (
            <div
              onClick={() => !isUploading && coverInputRef.current?.click()}
              className={`
                relative aspect-[9/16] max-h-72 rounded-2xl overflow-hidden transition-all
                border-2 border-dashed cursor-pointer
                ${isUploading
                  ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-300 bg-gradient-to-b from-gray-50/50 to-gray-100/30 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-50'
                }
              `}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
                {isUploading ? (
                  <>
                    <Loader2 className="w-10 h-10 text-gray-400 animate-spin" />
                    <span className="text-xs text-gray-500 font-medium">Przesyłanie...</span>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="w-7 h-7 text-gray-400" />
                    </div>
                    <div className="text-center">
                      <span className="text-sm font-medium text-gray-500 block">Dodaj okładkę</span>
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        1080×1920px
                      </span>
                    </div>
                  </>
                )}
              </div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onCoverImageChange}
                disabled={isUploading}
              />
            </div>
          ) : (
            <div className="relative aspect-[9/16] max-h-72 rounded-2xl overflow-hidden shadow-lg ring-2 ring-purple-200">
              <img
                src={coverImageUrl}
                alt="Reel cover"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={onCoverImageRemove}
                disabled={isUploading}
                className="absolute top-2 right-2 bg-red-500/90 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg transition-all hover:scale-110"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              {/* Cover badge */}
              <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                <ImageIcon className="w-2.5 h-2.5" />
                Okładka
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compact info bar */}
      <div className="flex items-center gap-3 px-3 py-2 bg-purple-50/70 border border-purple-100 rounded-xl text-[11px] text-purple-600">
        <Info className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          <strong>9:16</strong> (1080×1920) · <strong>{REELS_MAX_DURATION_SECONDS}s</strong> max · Opis do <strong>{REELS_MAX_CAPTION_LENGTH}</strong> znaków
        </span>
      </div>
    </div>
  );
};
